import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { msg } from '@lingui/core/macro';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchGuildMembers } from '../lib/api';
import { rememberLastServerId, loadLastServerId } from '../lib/serverSelection';
import { resolveDefaultChannel } from '../lib/defaultChannel';
import { routes } from '../lib/routes';
import { resetServerScopedStores, useAuthStore, useChannelStore, useServerStore } from '../stores';
import { commitServerChannelSnapshot } from '../stores/useChannelStore';

export type ServerTransition =
  | { status: 'idle'; retryToken: number }
  | { status: 'loading'; targetGuildId: string; landingPath: string | null; retryToken: number }
  | { status: 'error'; targetGuildId: string; error: unknown; retryToken: number };

type ServerTransitionAction =
  | { type: 'start'; targetGuildId: string }
  | { type: 'await-route'; targetGuildId: string; landingPath: string }
  | { type: 'fail'; targetGuildId: string; error: unknown }
  | { type: 'finish' }
  | { type: 'retry' };

export function reduceServerTransition(
  state: ServerTransition,
  action: ServerTransitionAction,
): ServerTransition {
  switch (action.type) {
    case 'start':
      return {
        status: 'loading',
        targetGuildId: action.targetGuildId,
        landingPath: null,
        retryToken: state.retryToken,
      };
    case 'await-route':
      return {
        status: 'loading',
        targetGuildId: action.targetGuildId,
        landingPath: action.landingPath,
        retryToken: state.retryToken,
      };
    case 'fail':
      return {
        status: 'error',
        targetGuildId: action.targetGuildId,
        error: action.error,
        retryToken: state.retryToken,
      };
    case 'retry':
      if (state.status !== 'error') return state;
      return {
        status: 'loading',
        targetGuildId: state.targetGuildId,
        landingPath: null,
        retryToken: state.retryToken + 1,
      };
    case 'finish':
      return { status: 'idle', retryToken: state.retryToken };
  }
}

interface ServerRouteIntent {
  guildId?: string;
  view: string;
}

interface ServerTransitionControllerOptions {
  authenticated: boolean;
  initialized: boolean;
  route: ServerRouteIntent;
  clearServerSubscriptions: () => void;
  onCommitServerContext: () => void;
}

interface ServerTransitionController {
  transition: ServerTransition;
  inaccessibleServer: boolean;
  fallbackGuildId: string | null;
  retry: () => void;
  setBootstrapInaccessible: (inaccessible: boolean) => void;
  markRemovedMembershipFallback: (guildId: string) => void;
}

/**
 * Owns the application-shell transition between committed Server contexts.
 *
 * The URL is route intent, while auth.guildId is the committed context. Target
 * resources stay local to this controller until both reads succeed; only then
 * are Server-scoped stores replaced and the new guild id published.
 */
export function useServerTransitionController({
  authenticated,
  initialized,
  route,
  clearServerSubscriptions,
  onCommitServerContext,
}: ServerTransitionControllerOptions): ServerTransitionController {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAuthStore((state) => state.currentUser);
  const guildId = useAuthStore((state) => state.guildId);
  const setGuildId = useAuthStore((state) => state.setGuildId);
  const channels = useChannelStore((state) => state.channels);
  const currentChannelId = useChannelStore((state) => state.currentChannelId);
  const setSnapshot = useChannelStore((state) => state.setSnapshot);
  const guilds = useServerStore((state) => state.guilds);
  const guildsLoaded = useServerStore((state) => state.guildsLoaded);
  const createRecovery = useServerStore((state) => state.createRecovery);
  const createTerminalGuildId = useServerStore((state) => state.createTerminalGuildId);
  const guildMembersLoaded = useServerStore((state) => state.loaded);
  const loadedMembersGuildId = useServerStore((state) => state.loadedGuildId);
  const membershipAuthorityRevision = useServerStore((state) => state.authorityRevision);
  const replaceMembers = useServerStore((state) => state.replaceMembers);

  const [transition, dispatch] = useReducer(reduceServerTransition, {
    status: 'idle',
    retryToken: 0,
  });
  const [inaccessibleServer, setInaccessibleServer] = useState(false);
  const [fallbackGuildId, setFallbackGuildId] = useState<string | null>(null);
  const fallbackGuildIdRef = useRef<string | null>(null);
  const routeGuildIdRef = useRef(route.guildId);
  routeGuildIdRef.current = route.guildId;

  const clearFallback = useCallback(() => {
    fallbackGuildIdRef.current = null;
    setFallbackGuildId(null);
  }, []);

  const setBootstrapInaccessible = useCallback(
    (inaccessible: boolean) => {
      setInaccessibleServer(inaccessible);
      if (inaccessible) {
        clearFallback();
        dispatch({ type: 'finish' });
      }
    },
    [clearFallback],
  );

  const markRemovedMembershipFallback = useCallback((nextGuildId: string) => {
    fallbackGuildIdRef.current = nextGuildId;
    setFallbackGuildId(nextGuildId);
  }, []);

  const retry = useCallback(() => dispatch({ type: 'retry' }), []);

  useEffect(() => {
    if (authenticated) return;
    dispatch({ type: 'finish' });
    setInaccessibleServer(false);
    clearFallback();
  }, [authenticated, clearFallback]);

  // Leaving a Server route cancels any staged switch and clears route-scoped
  // not-found/fallback state. Otherwise an inaccessible Server deep link can
  // poison a later global surface (for example /agents) with "Server not
  // found", or a pending fallback can pull that navigation back into a Server.
  // The cancellation flag in the load effect prevents a late response from
  // committing after navigation.
  useEffect(() => {
    if (route.guildId) return;
    if (transition.status !== 'idle') dispatch({ type: 'finish' });
    // A personal route is deliberately outside any Server context. Keeping the
    // previous committed id would leave its Server icon selected and preserve
    // an incorrect visual claim that My Tasks belongs to that Server.
    if (useAuthStore.getState().guildId !== null) {
      clearServerSubscriptions();
      resetServerScopedStores();
      setGuildId(null);
    }
    setInaccessibleServer(false);
    clearFallback();
  }, [route.guildId, transition.status, clearFallback, clearServerSubscriptions, setGuildId]);

  // Route intent and committed Server context are deliberately separate. The
  // route renders a loading boundary immediately when they differ; target data
  // is staged off-store and committed only after every required read succeeds.
  useEffect(() => {
    if (!initialized || !guildsLoaded || !route.guildId) return;
    const targetGuildId = route.guildId;
    const pendingFallbackGuildId = fallbackGuildIdRef.current;
    if (pendingFallbackGuildId) {
      if (targetGuildId === pendingFallbackGuildId) clearFallback();
      else return;
    }

    const accessible = guilds.some((candidate) => candidate.id === targetGuildId);
    if (!accessible) {
      const userId = currentUser?.id;
      const rememberedGuildId = userId ? loadLastServerId(userId) : null;
      if (
        targetGuildId !== createTerminalGuildId &&
        targetGuildId === rememberedGuildId &&
        guilds[0]
      ) {
        const nextFallbackGuildId = guilds[0].id;
        if (userId) rememberLastServerId(userId, nextFallbackGuildId);
        // If the missing membership is the committed context, revoke its live
        // subscriptions and stores before attempting the fallback. A failed
        // fallback load must never leave data from a Server the user no longer
        // belongs to installed behind an error boundary.
        if (targetGuildId === useAuthStore.getState().guildId) {
          clearServerSubscriptions();
          resetServerScopedStores();
          setGuildId(null);
        }
        setInaccessibleServer(false);
        markRemovedMembershipFallback(nextFallbackGuildId);
      } else {
        // A random inaccessible deep link must not destroy the coherent Server
        // context behind it. If the committed membership itself disappeared,
        // however, revoke its local data and subscriptions immediately.
        if (targetGuildId === useAuthStore.getState().guildId) {
          clearServerSubscriptions();
          resetServerScopedStores();
          setGuildId(null);
        }
        setInaccessibleServer(true);
        dispatch({ type: 'finish' });
        clearFallback();
      }
      return;
    }

    setInaccessibleServer(false);
    clearFallback();
    if (targetGuildId === useAuthStore.getState().guildId) {
      if (route.view !== 'redirect') dispatch({ type: 'finish' });
      return;
    }

    const accountId = currentUser?.id ?? null;
    const stagedAuthorityRevision = membershipAuthorityRevision;
    let cancelled = false;
    const controller = new AbortController();
    dispatch({ type: 'start', targetGuildId });

    commitServerChannelSnapshot(
      targetGuildId,
      (signal) => fetchGuildMembers(targetGuildId, signal),
      (channelResult, members) => {
        const targetStillCurrent =
          !cancelled &&
          routeGuildIdRef.current === targetGuildId &&
          useAuthStore.getState().currentUser?.id === accountId &&
          useServerStore.getState().authorityRevision === stagedAuthorityRevision &&
          useServerStore.getState().guilds.some((candidate) => candidate.id === targetGuildId);
        if (!targetStillCurrent) return;

        const landingChannel =
          route.view === 'redirect'
            ? resolveDefaultChannel(channelResult.channels, targetGuildId, 'onboarding')
            : null;
        const landingPath = landingChannel
          ? routes.channel(targetGuildId, landingChannel.id)
          : null;

        // Keep the old context coherent while requests are in flight. Once
        // every target resource is ready, invalidate stale work, install the
        // staged snapshot, and publish the new guild id last.
        clearServerSubscriptions();
        resetServerScopedStores();
        setSnapshot(channelResult, targetGuildId);
        replaceMembers(targetGuildId, members);
        onCommitServerContext();
        if (currentUser) rememberLastServerId(currentUser.id, targetGuildId);
        if (landingPath) dispatch({ type: 'await-route', targetGuildId, landingPath });
        setGuildId(targetGuildId);
        if (landingPath) navigate(landingPath, { replace: true });
        else dispatch({ type: 'finish' });
      },
      controller.signal,
    ).catch((error: unknown) => {
      if (cancelled || routeGuildIdRef.current !== targetGuildId) return;
      const recovery = useServerStore.getState().createRecovery;
      if (recovery?.kind === 'receipt' && recovery.guildId === targetGuildId) {
        useServerStore
          .getState()
          .setCreateError(
            msg`The Project was created, but its channels could not be loaded. Retry switching Projects.`,
          );
      }
      dispatch({ type: 'fail', targetGuildId, error });
    });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    initialized,
    guildsLoaded,
    route.guildId,
    route.view,
    guilds,
    guildId,
    currentUser,
    membershipAuthorityRevision,
    createTerminalGuildId,
    setGuildId,
    setSnapshot,
    replaceMembers,
    navigate,
    transition.retryToken,
    clearFallback,
    clearServerSubscriptions,
    markRemovedMembershipFallback,
    onCommitServerContext,
  ]);

  useEffect(() => {
    if (
      transition.status === 'loading' &&
      transition.landingPath &&
      transition.targetGuildId === guildId &&
      transition.landingPath === location.pathname
    ) {
      dispatch({ type: 'finish' });
    }
  }, [transition, guildId, location.pathname]);

  useEffect(() => {
    if (createRecovery?.kind !== 'receipt' || transition.status !== 'idle') return;
    const exactPath = routes.channel(createRecovery.guildId, createRecovery.landingChannelId);
    const settled =
      guildId === createRecovery.guildId &&
      location.pathname === exactPath &&
      channels.some(
        (channel) =>
          channel.id === createRecovery.landingChannelId &&
          channel.guild_id === createRecovery.guildId,
      ) &&
      guildMembersLoaded &&
      loadedMembersGuildId === createRecovery.guildId &&
      currentChannelId === createRecovery.landingChannelId;
    if (settled) useServerStore.getState().clearCreateReceipt();
  }, [
    createRecovery,
    transition.status,
    guildId,
    location.pathname,
    channels,
    guildMembersLoaded,
    loadedMembersGuildId,
    currentChannelId,
  ]);

  return {
    transition,
    inaccessibleServer,
    fallbackGuildId,
    retry,
    setBootstrapInaccessible,
    markRemovedMembershipFallback,
  };
}
